import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Loader2, Trash2, RefreshCw, Plug } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import ConnectFormRenderer, { type ConnectFormField, buildFormValues, extractConnectionParams } from "./ConnectFormRenderer";

interface Connector {
  id: string;
  name: string;
  db_type: string;
  enabled: boolean;
  logo: string | null;
  logo_color: string | null;
  created_at: string;
}

interface GenericConnectorPageProps {
  label: string;
  dbType: string;
}

const GenericConnectorPage = ({ label, dbType }: GenericConnectorPageProps) => {
  const { profile } = useAuth();
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Catalog info
  const [catalogLogo, setCatalogLogo] = useState<string | null>(null);
  const [catalogColor, setCatalogColor] = useState<string | null>(null);

  // Dynamic form from all_connectors.connect_form
  const [connectFormFields, setConnectFormFields] = useState<ConnectFormField[] | null>(null);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});

  const fetchConnectors = async () => {
    if (!profile?.client_id) return;
    setLoading(true);
    const { data } = await supabase
      .from("pipeline_source_connector")
      .select("id, name, db_type, enabled, logo, logo_color, created_at")
      .eq("client_id", profile.client_id)
      .eq("db_type", dbType as any)
      .order("created_at", { ascending: false });
    setConnectors((data as unknown as Connector[]) || []);
    setLoading(false);
  };

  // Fetch catalog logo/color and connect_form once
  useEffect(() => {
    const fetchCatalog = async () => {
      const { data } = await supabase
        .from("all_connectors")
        .select("logo, color, connect_form")
        .eq("db_type", dbType)
        .limit(1)
        .maybeSingle();
      if (data) {
        setCatalogLogo(data.logo);
        setCatalogColor(data.color);
        const fields = (data as any).connect_form as ConnectFormField[] | null;
        setConnectFormFields(fields);
      }
    };
    fetchCatalog();
  }, [dbType]);

  useEffect(() => {
    fetchConnectors();
  }, [profile?.client_id, dbType]);

  const openAddDialog = () => {
    setFormValues(connectFormFields ? buildFormValues(connectFormFields) : {});
    setAddOpen(true);
  };

  const handleAdd = async () => {
    const connName = connectFormFields
      ? ((formValues.name as string) || "").trim()
      : ((formValues.name as string) || "").trim();
    if (!connName) {
      toast.error("Connector name is required");
      return;
    }
    if (!profile?.client_id) return;
    setSaving(true);

    const connectionParams = connectFormFields
      ? extractConnectionParams(connectFormFields, formValues)
      : {};

    const { error } = await supabase.from("pipeline_source_connector").insert({
      client_id: profile.client_id,
      name: connName,
      db_type: dbType,
      connection_params: connectionParams,
      enabled: false,
      logo: catalogLogo || null,
      logo_color: catalogColor || null,
    } as any);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Connector added (disabled by default)");
      setAddOpen(false);
      setFormValues({});
      fetchConnectors();
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("pipeline_source_connector").delete().eq("id", id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Connector deleted");
      setConnectors((prev) => prev.filter((c) => c.id !== id));
    }
  };

  const toggleEnabled = async (id: string, enabled: boolean) => {
    const { error } = await supabase
      .from("pipeline_source_connector")
      .update({ enabled } as any)
      .eq("id", id);
    if (error) {
      toast.error(error.message);
    } else {
      setConnectors((prev) => prev.map((c) => (c.id === id ? { ...c, enabled } : c)));
    }
  };

  const hasConnectForm = connectFormFields && connectFormFields.length > 0;
  const connName = ((formValues.name as string) || "").trim();

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">{label}</h2>
          <p className="text-sm text-muted-foreground">Manage your {label} connectors</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchConnectors} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4 mr-1", loading && "animate-spin")} />
            Refresh
          </Button>
          <Button size="sm" onClick={openAddDialog}>
            <Plus className="h-4 w-4 mr-1" />
            Add Connector
          </Button>
        </div>
      </div>

      {/* Connector List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground gap-2 text-sm">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading connectors...
        </div>
      ) : connectors.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
          <Plug className="h-10 w-10" />
          <p className="text-sm">No {label} connectors configured yet</p>
          <Button size="sm" variant="outline" onClick={openAddDialog}>
            <Plus className="h-4 w-4 mr-1" />
            Add your first connector
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {connectors.map((conn) => (
            <div
              key={conn.id}
              className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <ConnectorLogo logo={conn.logo} logoColor={conn.logo_color} />
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{conn.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(conn.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge
                  variant={conn.enabled ? "default" : "secondary"}
                  className={cn(
                    "cursor-pointer text-xs",
                    conn.enabled ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30" : "",
                  )}
                  onClick={() => toggleEnabled(conn.id, !conn.enabled)}
                >
                  {conn.enabled ? "Enabled" : "Disabled"}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-red-400"
                  onClick={() => handleDelete(conn.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Connector Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className={cn("sm:max-w-md", hasConnectForm && "sm:max-w-lg")}>
          <DialogHeader>
            <DialogTitle>Add {label} Connector</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            {hasConnectForm ? (
              <ConnectFormRenderer
                fields={connectFormFields}
                values={formValues}
                onChange={(name, value) => setFormValues((prev) => ({ ...prev, [name]: value }))}
              />
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Connector Name</Label>
                  <Input
                    value={(formValues.name as string) ?? ""}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder={`My ${label} connector`}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Connection URL / Connection String</Label>
                  <Input
                    value={(formValues.connection_string as string) ?? ""}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, connection_string: e.target.value }))}
                    placeholder="https://... or connection string"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={saving || !connName}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ConnectorLogo = ({ logo, logoColor }: { logo: string | null; logoColor: string | null }) => {
  const [imgError, setImgError] = useState(false);

  if (logo && !imgError) {
    return (
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg overflow-hidden"
        style={{ backgroundColor: logoColor || "#6366f1" }}
      >
        <img
          src={`https://cdn.simpleicons.org/${logo}/white`}
          alt=""
          style={{ width: 18, height: 18 }}
          className="object-contain"
          onError={() => setImgError(true)}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
      style={{ backgroundColor: logoColor || "#6366f1" }}
    >
      <Plug className="h-4 w-4 text-white" />
    </div>
  );
};

export default GenericConnectorPage;
